import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { WorkspaceDraft, DraftAdSet, DraftAd } from '@/services/ai-campaign.service';

// ─── Types ───────────────────────────────────────────────────────────────────

export type EntityStatus = 'DRAFT' | 'SAVED' | 'PUBLISHED';
export type ActiveLevel = 'campaign' | 'adset' | 'ad';
/**
 * Which surface the workspace is being driven for. The data shape is
 * identical for both (Meta has no separate "Instagram campaign" object —
 * IG-vs-FB is a per-ad-set targeting decision), but the workspace UI
 * locks placements to Instagram-only and forces the IG actor on the
 * creative when this is `'instagram'`.
 */
export type WorkspacePlatform = 'facebook' | 'instagram';
export interface RegistryAppPromotion {
  appUid: string; companyUid: string; name: string; platform: 'android' | 'ios';
  metaAppId: string; storeUrl: string; accountUid: string; accountName: string; accountCurrency: string;
}

export interface WorkspaceAd {
  id: string;
  name: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
  metaId?: string;
}

export interface WorkspaceAdSet {
  id: string;
  name: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
  metaId?: string;
  ads: WorkspaceAd[];
}

export interface WorkspaceCampaign {
  id: string;
  name: string;
  objective: string;
  status: EntityStatus;
  formData: Record<string, unknown>;
  isLocked: boolean;
  apiId?: string;
  metaId?: string;
}

export interface WorkspaceState {
  draftId: string;
  activeLevel: ActiveLevel;
  activeAdSetId: string | null;
  activeAdId: string | null;
  campaign: WorkspaceCampaign;
  adSets: WorkspaceAdSet[];
  isSubmitting: boolean;
  aiGenerated: boolean;
  aiPublishedAt: string | null;
  /**
   * Set by the provider on mount based on whether the workspace is
   * mounted under /facebook/workspace or /instagram/workspace. Not
   * mutated by reducers — purely a routing/lock flag read by child
   * forms (placement picker, ad form IG-actor enforcement, etc.).
   */
  platform: WorkspacePlatform;
}

interface ContextValue extends WorkspaceState {
  appPromotion?: RegistryAppPromotion;
  // Navigation
  setActiveLevel: (level: ActiveLevel, adSetId?: string | null, adId?: string | null) => void;

  // Campaign
  updateCampaignForm: (partial: Partial<WorkspaceCampaign['formData']>) => void;
  lockCampaign: (apiId: string, metaId?: string) => void;
  unlockCampaign: () => void;

  // Ad sets
  addAdSet: () => string;
  updateAdSetForm: (id: string, partial: Partial<WorkspaceAdSet['formData']>) => void;
  lockAdSet: (id: string, apiId: string, metaId?: string) => void;
  unlockAdSet: (id: string) => void;
  deleteAdSet: (id: string) => void;
  duplicateAdSet: (id: string) => string;

  // Ads
  addAd: (adSetId: string) => string;
  updateAdForm: (adSetId: string, adId: string, partial: Partial<WorkspaceAd['formData']>) => void;
  lockAd: (adSetId: string, adId: string, apiId: string, metaId?: string) => void;
  unlockAd: (adSetId: string, adId: string) => void;
  deleteAd: (adSetId: string, adId: string) => void;
  duplicateAd: (adSetId: string, adId: string) => string;

  // UI
  setIsSubmitting: (v: boolean) => void;

  // Draft
  saveDraft: () => void;
  discardDraft: () => void;

  // Computed
  activeAdSet: WorkspaceAdSet | null;
  activeAd: WorkspaceAd | null;
  canPublish: boolean;

  // Reset
  reset: () => void;

  // AI draft
  applyDraft: (draft: WorkspaceDraft) => void;
  aiGenerated: boolean;
  aiPublishedAt: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
// Separate localStorage keys per platform so a half-finished IG draft and a
// half-finished FB draft don't overwrite each other. Resuming `/facebook/workspace`
// must restore the FB draft only, even if the user has IG work-in-progress.
const DRAFT_KEY_BY_PLATFORM: Record<WorkspacePlatform, string> = {
  facebook: 'slightercrm.fb-workspace-draft',
  instagram: 'slightercrm.ig-workspace-draft',
};

function defaultCampaign(): WorkspaceCampaign {
  return { id: genId(), name: '', objective: '', status: 'DRAFT', formData: {}, isLocked: false };
}

function defaultAdSet(name = 'Ad Set 1'): WorkspaceAdSet {
  return { id: genId(), name, status: 'DRAFT', formData: {}, isLocked: false, ads: [] };
}

function defaultAd(name = 'Ad 1'): WorkspaceAd {
  return { id: genId(), name, status: 'DRAFT', formData: {}, isLocked: false };
}

function defaultState(platform: WorkspacePlatform = 'facebook', appPromotion?: RegistryAppPromotion): WorkspaceState {
  const campaign = defaultCampaign();
  if (appPromotion) {
    campaign.name = `${appPromotion.name} · App promotion`;
    campaign.objective = 'OUTCOME_APP_PROMOTION';
    campaign.formData = { name: campaign.name, objective: campaign.objective };
  }
  return {
    draftId: genId(),
    activeLevel: 'campaign',
    activeAdSetId: null,
    activeAdId: null,
    campaign,
    adSets: [],
    isSubmitting: false,
    aiGenerated: false,
    aiPublishedAt: null,
    platform,
  };
}

interface PersistedDraft {
  savedAt: number;
  state: WorkspaceState;
}

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadDraft(storageKey: string): { state: WorkspaceState; savedAt: number } | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft | WorkspaceState;
    // Back-compat: older format stored the bare WorkspaceState. Detect by
    // presence of `savedAt` and adapt.
    if ((parsed as PersistedDraft).savedAt !== undefined && (parsed as PersistedDraft).state) {
      const p = parsed as PersistedDraft;
      if (Date.now() - p.savedAt > DRAFT_TTL_MS) return null; // stale → drop
      return { state: p.state, savedAt: p.savedAt };
    }
    // Legacy bare-state record. Treat as fresh (we don't know its age).
    return { state: parsed as WorkspaceState, savedAt: Date.now() };
  } catch {
    return null;
  }
}

/**
 * A draft is considered "meaningful" (i.e. worth restoring without an
 * explicit resumeDraftId) when the user has actually typed something.
 * Auto-restoring an empty defaultState would feel like a no-op but
 * still suppress legitimate first-time empty workspaces, so we filter.
 */
function isMeaningfulDraft(s: WorkspaceState): boolean {
  if (s.campaign.name) return true;
  if (s.campaign.objective) return true;
  if (s.adSets.length > 0) return true;
  if (Object.keys(s.campaign.formData || {}).length > 0) return true;
  return false;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const Ctx = createContext<ContextValue | null>(null);

export function FbWorkspaceProvider({
  children,
  resumeDraftId,
  platform = 'facebook',
  appPromotion,
}: {
  children: React.ReactNode;
  resumeDraftId?: string;
  appPromotion?: RegistryAppPromotion;
  /**
   * Which Meta surface this workspace is configured for. Mounted from the
   * route — `/facebook/workspace` → 'facebook', `/instagram/workspace` →
   * 'instagram'. Drives placement lock + IG-actor enforcement downstream.
   */
  platform?: WorkspacePlatform;
}) {
  const storageKey = appPromotion
    ? `${DRAFT_KEY_BY_PLATFORM[platform]}:${appPromotion.companyUid}:app:${appPromotion.appUid}:account:${appPromotion.accountUid}`
    : DRAFT_KEY_BY_PLATFORM[platform];
  // Restore on mount:
  //  - With an explicit `:draftId` URL param: restore only if the saved
  //    draftId matches (existing behavior, used by deep links).
  //  - Without one: restore any meaningful, non-stale draft. This is the
  //    fix for "I opened /facebook/workspace and lost my work" when a user
  //    navigates away (e.g. to Ad Account Settings to fix Pages) and back.
  // Note: draft restore is platform-scoped — FB and IG keep separate drafts
  // so a half-finished IG campaign doesn't clobber a half-finished FB one.
  const [state, setState] = useState<WorkspaceState>(() => {
    const saved = loadDraft(storageKey);
    if (resumeDraftId) {
      if (saved && saved.state.draftId === resumeDraftId) return { ...saved.state, isSubmitting: false, platform };
      return defaultState(platform, appPromotion);
    }
    if (saved && isMeaningfulDraft(saved.state)) return { ...saved.state, isSubmitting: false, platform };
    return defaultState(platform, appPromotion);
  });

  // Auto-save on every state change (debounced 1.5s). Wrapped in a
  // `{savedAt, state}` envelope so we can age out stale drafts.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const envelope: PersistedDraft = { savedAt: Date.now(), state };
        localStorage.setItem(storageKey, JSON.stringify(envelope));
      } catch { /* quota exceeded */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [state, storageKey]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const setActiveLevel = useCallback((level: ActiveLevel, adSetId?: string | null, adId?: string | null) => {
    setState(prev => ({
      ...prev,
      activeLevel: level,
      activeAdSetId: adSetId !== undefined ? adSetId : prev.activeAdSetId,
      activeAdId: adId ?? null,
    }));
  }, []);

  // ── Campaign ────────────────────────────────────────────────────────────────
  const updateCampaignForm = useCallback((partial: Partial<WorkspaceCampaign['formData']>) => {
    setState(prev => {
      if (prev.campaign.isLocked) return prev;
      const fd = { ...prev.campaign.formData, ...partial } as Record<string, unknown>;
      if (appPromotion) fd.objective = 'OUTCOME_APP_PROMOTION';
      const name = (fd.name as string) || prev.campaign.name;
      const objective = (fd.objective as string) || prev.campaign.objective;
      return { ...prev, campaign: { ...prev.campaign, formData: fd, name, objective } };
    });
  }, [appPromotion]);

  const lockCampaign = useCallback((apiId: string, metaId?: string) => {
    setState(prev => ({
      ...prev,
      campaign: { ...prev.campaign, isLocked: true, status: 'SAVED', apiId, metaId },
    }));
  }, []);

  const unlockCampaign = useCallback(() => {
    setState(prev => ({ ...prev, campaign: { ...prev.campaign, isLocked: false } }));
  }, []);

  // ── Ad Sets ─────────────────────────────────────────────────────────────────
  const addAdSet = useCallback((): string => {
    const idx = (prev: WorkspaceState) => prev.adSets.length + 1;
    let newId = '';
    setState(prev => {
      const as = defaultAdSet(`Ad Set ${idx(prev)}`);
      newId = as.id;
      return {
        ...prev,
        adSets: [...prev.adSets, as],
        activeLevel: 'adset',
        activeAdSetId: as.id,
        activeAdId: null,
      };
    });
    return newId;
  }, []);

  const updateAdSetForm = useCallback((id: string, partial: Partial<WorkspaceAdSet['formData']>) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as => {
        if (as.id !== id || as.isLocked) return as;
        const fd = { ...as.formData, ...partial } as Record<string, unknown>;
        const name = (fd.name as string) || as.name;
        return { ...as, formData: fd, name };
      }),
    }));
  }, []);

  const lockAdSet = useCallback((id: string, apiId: string, metaId?: string) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as =>
        as.id === id ? { ...as, isLocked: true, status: 'SAVED' as EntityStatus, apiId, metaId } : as,
      ),
    }));
  }, []);

  const unlockAdSet = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as => as.id === id ? { ...as, isLocked: false } : as),
    }));
  }, []);

  const deleteAdSet = useCallback((id: string) => {
    setState(prev => {
      const as = prev.adSets.find(x => x.id === id);
      if (as?.isLocked) return prev;
      const newSets = prev.adSets.filter(x => x.id !== id);
      const wasActive = prev.activeAdSetId === id;
      return {
        ...prev,
        adSets: newSets,
        activeLevel: wasActive ? 'campaign' : prev.activeLevel,
        activeAdSetId: wasActive ? null : prev.activeAdSetId,
        activeAdId: wasActive ? null : prev.activeAdId,
      };
    });
  }, []);

  const duplicateAdSet = useCallback((id: string): string => {
    let newId = '';
    setState(prev => {
      const orig = prev.adSets.find(x => x.id === id);
      if (!orig) return prev;
      const dup: WorkspaceAdSet = {
        ...orig,
        id: genId(),
        name: `${orig.name} (Copy)`,
        status: 'DRAFT',
        isLocked: false,
        apiId: undefined,
        metaId: undefined,
        ads: orig.ads.map(ad => ({
          ...ad,
          id: genId(),
          name: `${ad.name} (Copy)`,
          status: 'DRAFT' as EntityStatus,
          isLocked: false,
          apiId: undefined,
          metaId: undefined,
        })),
      };
      newId = dup.id;
      return { ...prev, adSets: [...prev.adSets, dup], activeAdSetId: dup.id, activeLevel: 'adset', activeAdId: null };
    });
    return newId;
  }, []);

  // ── Ads ─────────────────────────────────────────────────────────────────────
  const addAd = useCallback((adSetId: string): string => {
    let newId = '';
    setState(prev => {
      const as = prev.adSets.find(x => x.id === adSetId);
      if (!as) return prev;
      const adIdx = as.ads.length + 1;
      const ad = defaultAd(`Ad ${adIdx}`);
      if (appPromotion) ad.formData = { link: appPromotion.storeUrl, cta: 'INSTALL_MOBILE_APP', creative_name: `${appPromotion.name} creative` };
      newId = ad.id;
      return {
        ...prev,
        adSets: prev.adSets.map(x =>
          x.id === adSetId ? { ...x, ads: [...x.ads, ad] } : x,
        ),
        activeLevel: 'ad',
        activeAdSetId: adSetId,
        activeAdId: ad.id,
      };
    });
    return newId;
  }, [appPromotion]);

  const updateAdForm = useCallback((adSetId: string, adId: string, partial: Partial<WorkspaceAd['formData']>) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as =>
        as.id !== adSetId ? as : {
          ...as,
          ads: as.ads.map(ad => {
            if (ad.id !== adId || ad.isLocked) return ad;
            const fd = { ...ad.formData, ...partial } as Record<string, unknown>;
            const name = (fd.name as string) || ad.name;
            return { ...ad, formData: fd, name };
          }),
        },
      ),
    }));
  }, []);

  const lockAd = useCallback((adSetId: string, adId: string, apiId: string, metaId?: string) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as =>
        as.id !== adSetId ? as : {
          ...as,
          ads: as.ads.map(ad =>
            ad.id === adId ? { ...ad, isLocked: true, status: 'SAVED' as EntityStatus, apiId, metaId } : ad,
          ),
        },
      ),
    }));
  }, []);

  const unlockAd = useCallback((adSetId: string, adId: string) => {
    setState(prev => ({
      ...prev,
      adSets: prev.adSets.map(as =>
        as.id !== adSetId ? as : {
          ...as,
          ads: as.ads.map(ad => ad.id === adId ? { ...ad, isLocked: false } : ad),
        },
      ),
    }));
  }, []);

  const deleteAd = useCallback((adSetId: string, adId: string) => {
    setState(prev => {
      const as = prev.adSets.find(x => x.id === adSetId);
      const ad = as?.ads.find(x => x.id === adId);
      if (ad?.isLocked) return prev;
      const wasActive = prev.activeAdId === adId;
      return {
        ...prev,
        adSets: prev.adSets.map(x =>
          x.id !== adSetId ? x : { ...x, ads: x.ads.filter(a => a.id !== adId) },
        ),
        activeLevel: wasActive ? 'adset' : prev.activeLevel,
        activeAdId: wasActive ? null : prev.activeAdId,
      };
    });
  }, []);

  const duplicateAd = useCallback((adSetId: string, adId: string): string => {
    let newId = '';
    setState(prev => {
      const as = prev.adSets.find(x => x.id === adSetId);
      const orig = as?.ads.find(x => x.id === adId);
      if (!orig) return prev;
      const dup: WorkspaceAd = {
        ...orig,
        id: genId(),
        name: `${orig.name} (Copy)`,
        status: 'DRAFT',
        isLocked: false,
        apiId: undefined,
        metaId: undefined,
      };
      newId = dup.id;
      return {
        ...prev,
        adSets: prev.adSets.map(x =>
          x.id !== adSetId ? x : { ...x, ads: [...x.ads, dup] },
        ),
        activeAdId: dup.id,
        activeLevel: 'ad',
      };
    });
    return newId;
  }, []);

  // ── UI ───────────────────────────────────────────────────────────────────────
  const setIsSubmitting = useCallback((v: boolean) => {
    setState(prev => ({ ...prev, isSubmitting: v }));
  }, []);

  // ── Draft management ─────────────────────────────────────────────────────────
  const saveDraft = useCallback(() => {
    try {
      // Write to the platform-scoped key so an FB draft and IG draft can
      // co-exist. The bare-state shape kept for back-compat with older
      // sessions that didn't wrap in {savedAt, state}.
      localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), state }));
    } catch { /* ignore */ }
  }, [state, storageKey]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    setState(defaultState(state.platform, appPromotion));
  }, [state.platform, storageKey, appPromotion]);

  const reset = useCallback(() => {
    localStorage.removeItem(storageKey);
    setState(defaultState(state.platform, appPromotion));
  }, [state.platform, storageKey, appPromotion]);

  // ── AI draft application ──────────────────────────────────────────────────────
  const applyDraft = useCallback((draft: WorkspaceDraft) => {
    const campaignFormData: Record<string, unknown> = { ...(draft.campaign as unknown as Record<string, unknown>) };
    const newCampaign: WorkspaceCampaign = {
      id: genId(),
      name: draft.campaign.name,
      objective: draft.campaign.objective,
      status: 'DRAFT',
      formData: campaignFormData,
      isLocked: false,
    };

    const newAdSets: WorkspaceAdSet[] = (draft.adsets ?? []).map((as: DraftAdSet, asIdx: number) => {
      const ads: WorkspaceAd[] = (as.ads ?? []).map((ad: DraftAd, adIdx: number) => ({
        id: genId(),
        name: ad.name || `Ad ${adIdx + 1}`,
        status: 'DRAFT' as EntityStatus,
        formData: { ...(ad as unknown as Record<string, unknown>) },
        isLocked: false,
      }));

      return {
        id: genId(),
        name: as.name || `Ad Set ${asIdx + 1}`,
        status: 'DRAFT' as EntityStatus,
        formData: { ...(as as unknown as Record<string, unknown>) },
        isLocked: false,
        ads,
      };
    });

    const firstAdSet = newAdSets[0] ?? null;
    setState(prev => ({
      draftId: genId(),
      activeLevel: 'campaign',
      activeAdSetId: firstAdSet?.id ?? null,
      activeAdId: null,
      campaign: newCampaign,
      adSets: newAdSets,
      isSubmitting: false,
      aiGenerated: true,
      aiPublishedAt: null,
      // Preserve the current platform when an AI draft is applied — the
      // user is still in the same /facebook/workspace or /instagram/workspace
      // route. Otherwise applying a draft would silently revert to 'facebook'.
      platform: prev.platform,
    }));
  }, []);

  // ── Computed ─────────────────────────────────────────────────────────────────
  const activeAdSet = useMemo(
    () => state.adSets.find(x => x.id === state.activeAdSetId) ?? null,
    [state.adSets, state.activeAdSetId],
  );

  const activeAd = useMemo(
    () => (!activeAdSet || !state.activeAdId ? null : activeAdSet.ads.find(x => x.id === state.activeAdId) ?? null),
    [activeAdSet, state.activeAdId],
  );

  const canPublish = useMemo(() => {
    if (appPromotion) return false;
    if (!state.campaign.isLocked) return false;
    const lockedSet = state.adSets.find(x => x.isLocked);
    if (!lockedSet) return false;
    return !!lockedSet.ads.find(x => x.isLocked);
  }, [state.campaign.isLocked, state.adSets, appPromotion]);

  const value: ContextValue = {
    ...state,
    appPromotion,
    setActiveLevel,
    updateCampaignForm,
    lockCampaign,
    unlockCampaign,
    addAdSet,
    updateAdSetForm,
    lockAdSet,
    unlockAdSet,
    deleteAdSet,
    duplicateAdSet,
    addAd,
    updateAdForm,
    lockAd,
    unlockAd,
    deleteAd,
    duplicateAd,
    setIsSubmitting,
    saveDraft,
    discardDraft,
    activeAdSet,
    activeAd,
    canPublish,
    reset,
    applyDraft,
    aiGenerated: state.aiGenerated,
    aiPublishedAt: state.aiPublishedAt,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFbWorkspace(): ContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFbWorkspace must be used inside FbWorkspaceProvider');
  return ctx;
}
